import { IGeoLocation } from './geotracking';
import { INameObject } from './index';

export type IRevenueItem = {
    currency_code: string;
    amount: string;
};

export type IDashboardType = 'Main' | 'Finances';

export interface IDashboardTypes {
    [id: string]: IDashboardType;
}
export const dashboardType: IDashboardTypes = {
    main: 'Main',
    finances: 'Finances',
};

export type IChartTitleItem =
    | string
    | { role: 'annotationText' | 'annotation' | 'tooltip' | 'id'; type?: 'string'; p?: { html?: true } };
export type IChartTitle = Array<IChartTitleItem>;
export type IChartItemData = Array<string | number | undefined>;
export type IChartData = Array<IChartTitle | IChartItemData>;

export type IUsageMap = Array<ICardSetUsage> | Array<ICardsUsage> | Array<ICountryUsage> | Array<IUsageData>;

export type ICartItemData = {
    [id: string]: string | number;
};

export type IRankItemStatsAPI = {
    currency_id: number | null;
    currency_abbreviation: string | null;
    currency_symbol: string | null;
    cost: number | string | null;
    revenue: number | string | null;
};
export type IRankItemAPI = {
    name: string | null;
    stats: Array<IRankItemStatsAPI> | null;
};
export type IRankCardRevenueItemAPI = IRankItemAPI & {
    card_id: number | null;
    eid: string | null;
    printed_id: string | null;
};
export type IRankCardSetRevenueItemAPI = IRankItemAPI & {
    card_set_id: number | null;
};
export type IRankCompanyRevenueItemAPI = IRankItemAPI & {
    company_id: number | null;
};
export type IRankCountryRevenueItemAPI = IRankItemAPI & {
    country_id: number | null;
    iso3: string | null;
    printed_id: string | null;
};
export type IRankOperatorRevenueItemAPI = IRankItemAPI & {
    operator_id: number | null;
};
export type IRankTimelineRevenueItemAPI = IRankItemAPI & {
    start_date: string | null;
    month: string | null;
};
export type IRankZoneRevenueItemAPI = IRankItemAPI & {
    zone_id: number | null;
};

export type IStatItemAPI = {
    cost?: string;
    currency_abbreviation?: string;
    currency_id?: number;
    currency_symbol?: string;
    revenue?: string;
};
export type IStatItem = {
    cost?: string;
    currencyAbbreviation?: string;
    currencyId?: number;
    currencySymbol?: string;
    revenue?: string;
};

export type ICostRevenueItem = {
    name?: string;
    stats?: Array<IStatItem>;
};

export type ICardSetRevenueItem = ICostRevenueItem & { cardSetId?: number };
export type ICardsRevenueItem = ICostRevenueItem & { cardId?: number; eid?: string; printedId?: string };
export type ICompanyRevenueItem = ICostRevenueItem & { companyId?: number };
export type ICountryRevenueItem = ICostRevenueItem & { countryId?: number; iso3?: string };
export type IOperatorRevenueItem = ICostRevenueItem & { operatorId?: number };
export type ITimelineRevenueItem = ICostRevenueItem & { startDate?: string; month?: string };
export type ITimelineZoneItem = ICostRevenueItem & { zoneId?: number };

export type IRevenueItemANY = ICardSetRevenueItem &
    ICardsRevenueItem &
    ICompanyRevenueItem &
    ICountryRevenueItem &
    IOperatorRevenueItem &
    ITimelineRevenueItem &
    ITimelineZoneItem;

export interface IUsageActivityUnitType {
    unitType?: string;
    volume?: number;
    volumeRatio?: number;
}
export interface IUsageActivityUnitTypeAPI {
    unit_type?: string;
    volume?: string;
    volume_ratio?: string;
}
export interface IUsageActivityRollAPI {
    cards_count?: number;
    cards_ratio?: number;
    cost_ratio?: string;
    costs?: Array<IStatItemAPI>;
    stats_by_unit_type?: Array<IUsageActivityUnitTypeAPI>;
}
export interface IUsageActivityRoll {
    cardsCount?: number;
    cardsRatio?: number;
    costRatio?: number;
    costs: Array<IStatItem>;
    statsByUnitType: Array<IUsageActivityUnitType>;
}
export interface IFinancesRollAPI {
    cards?: IFinancesRollCardsAPI;
    costs_and_revenues?: Array<IStatItemAPI>;
    volume?: string;
}

export type IFinancesRollCardsAPI = {
    active?: number;
    inactive?: number;
};
export type IFinancesRollCards = {
    active: number;
    inactive: number;
};
export type IFinancesRollCardsHook = IFinancesRollCards & {
    total: number;
    pieActive: number;
};

export type IUsageDataKeys = keyof IUsageData;

export type IUsageData = {
    id?: number | null;
    company_id?: number;
    card_set_id?: number;
    currency_id?: number;
    operator_id?: number;
    subscriber_id?: number;
    card_id?: number;
    network_id?: number;
    country_id?: number;
    zone_id?: number;
    volume?: number | string;
    cost?: number;
    count?: number;
    name?: string;
    month?: string;
    start_date?: string;
    iso3?: string;
};

export interface ICardSetUsage {
    card_set_id?: number;
    card_set_name?: string;
    original_units?: string;
}
export interface ICardsUsage {
    card_id?: number;
    card_printed_id?: string;
    original_units?: string;
}
export interface ICompanyUsage {
    company_id?: number;
    company_name?: string;
    original_units?: string;
}
export interface ITelecomUsage {
    operator_id?: number;
    operator_name?: string;
    original_units?: string;
}
export interface IZoneUsage {
    zone_id?: number;
    zone_name?: string;
    original_units?: string;
}
export interface ITimelineUsage {
    time?: string;
    original_units?: string;
    month?: string;
}
export interface ICountryUsage {
    country_id?: number;
    country_name?: string;
    country_iso3?: string;
    original_units?: string;
}

export interface IDashboardActivityRoll {
    cardsCount?: number;
    cardsRatio?: number;
    costRatio?: number;
    costs: Array<IStatItem>;
    originalUnits?: number;
    originalUnitsRatio?: number;
    sms?: number;
    smsRatio?: number;
    voice?: number;
    voiceRatio?: number;
}

export type IGoogleGeoPosition = {
    lat?: number;
    lng?: number;
};

export interface ICardLocationMapInfo extends IGeoLocation, INameObject {}

export type IDashboardCountryUsageList = Array<ICountryUsageListItem>;

export interface ICountryUsageListItem {
    countryId?: number;
    countryName?: string;
    countryIso3?: string;
    originalUnits?: string;
}

export type IJsonCountryBorderPoint = number[];
export type IJsonCountryBorderPoints = IJsonCountryBorderPoint[];
export type IJsonCountryBordersPoints = IJsonCountryBorderPoints[];
export type IJsonCountryBorder = IJsonCountryBordersPoints | IJsonCountryBorderPoints;

export type IJSONCountryInfo = {
    alpha3?: string | undefined | null;
    ioc?: string | undefined | null;
    geo?: {
        center: IGoogleGeoPosition;
    };
};

export type IUseDashboardCountryUsageCountryInfo = {
    id: number;
    code?: string;
    name?: string;
    originalUnits?: string;
    textUnits?: string;
    geo?: {
        center: IGoogleGeoPosition;
        border: Array<IGoogleGeoPosition>;
    };
};

export type IUseDashboardCountryUsageCountryPolygonsList = Array<IUseDashboardCountryUsageCountryPolygon>;
export type IUseDashboardCountryUsageCountryPolygon = {
    id: number;
    polygonId: string;
    code?: string;
    name?: string;
    originalUnits?: string;
    textUnits?: string;
    strokeColor?: string;
    fillColor?: string;
    geo?: {
        border?: Array<IGoogleGeoPosition>;
    };
};

export interface ICurrenciesRevenue {
    [id: string]: number;
}

export interface ITextCurrenciesRevenue {
    [id: string]: string;
}

export type ICost = {
    cost?: string;
    currencyCode?: string;
    isRange?: boolean;
};

export type IOverlayView = JSX.Element | null;
